import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { API_BASE_URL } from '../../config/constants';
import { getBackofficeAuthHeaders } from '../../services/api';

export interface Plant {
  id: string;
  epicor_code: string;
  name: string;
  is_active: boolean;
}

interface Props {
  open: boolean;
  orderId: string;
  orderNumber: string;
  currentPlantCode?: string | null;
  onClose: () => void;
  onAssigned: (plantCode: string, plantName: string) => void;
}

async function api<T = any>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...getBackofficeAuthHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    let detail = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      detail = body?.detail || detail;
    } catch { /* non-JSON */ }
    throw new Error(detail);
  }
  return res.json();
}

const PlantAssignDialog: React.FC<Props> = ({
  open, orderId, orderNumber, currentPlantCode, onClose, onAssigned,
}) => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [selected, setSelected] = useState<string>(currentPlantCode || '');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load plants when the dialog opens. Refetched each time so a
  // mid-session plant sync surfaces new warehouses without a page
  // refresh.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSelected(currentPlantCode || '');
    api<{ plants: Plant[] }>('/backoffice/plants')
      .then((r) => {
        if (cancelled) return;
        setPlants(r.plants || []);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, currentPlantCode]);

  const refreshFromEpicor = async () => {
    setError(null);
    setLoading(true);
    try {
      await api('/backoffice/plants/sync', { method: 'POST' });
      const r = await api<{ plants: Plant[] }>('/backoffice/plants');
      setPlants(r.plants || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const submit = async () => {
    if (!selected) {
      setError('Pick a plant before saving.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const r = await api<{ plant_code: string; plant_name: string }>(
        `/backoffice/orders/${orderId}/plant`,
        { method: 'PATCH', body: JSON.stringify({ plant_code: selected }) },
      );
      onAssigned(r.plant_code, r.plant_name);
      onClose();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign fulfilment plant — {orderNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Typography variant="body2" color="text.secondary">
            Pick the warehouse that will fulfil this order. The selection
            is sent to Epicor as the SalesOrder.Plant when the sync runs.
            You can only set this while the order is in DRAFT state.
          </Typography>

          <FormControl fullWidth>
            <InputLabel id="plant-select">Plant</InputLabel>
            <Select
              labelId="plant-select"
              label="Plant"
              value={selected}
              onChange={(e) => setSelected(e.target.value as string)}
              disabled={loading || plants.length === 0}
            >
              {plants.map((p) => (
                <MenuItem key={p.epicor_code} value={p.epicor_code}>
                  <Box>
                    <Typography variant="body2" component="span" fontWeight={500}>
                      {p.epicor_code}
                    </Typography>
                    <Typography component="span" variant="body2" color="text.secondary"> — {p.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {plants.length === 0 && !loading && (
            <Alert severity="warning">
              No plants in the cache yet. Click "Refresh from Epicor" below.
            </Alert>
          )}

          <Box display="flex" justifyContent="flex-end">
            <Button
              size="small"
              onClick={refreshFromEpicor}
              disabled={loading || submitting}
            >
              {loading ? <CircularProgress size={18} /> : 'Refresh from Epicor'}
            </Button>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button onClick={submit} variant="contained" disabled={submitting || !selected}>
          {submitting ? <CircularProgress size={20} /> : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlantAssignDialog;
